/**
 * <TutorAvailabilityDialog> — les boîtes de « Ma dispo » et de « Libérer des créneaux » (template).
 *
 * Six cas, une seule forme : un titre, une phrase (une partie en gras quand elle porte la date ou
 * la durée), et au plus deux boutons empilés, le principal au-dessus. Les mots sont ceux du legacy,
 * mot pour mot — ils ont été repris plusieurs fois et sont porteurs :
 *  - `startingDate` — « Changer ta date de démarrage ? » (`MaDispoScreen`, la feuille de la date) ;
 *  - `continuity` — « Un créneau régulier, c'est un engagement » (`MaDispoContinuitySheet`) : après
 *    la première heure posée, c'est un accusé (« J'ai compris », pas d'« Annuler » qui ferait croire
 *    à un retour arrière) ; avant l'enregistrement, « Confirmer ma dispo » ; à l'étape du profil,
 *    « Continuer » ;
 *  - `leave` — « Enregistrer tes modifications ? », à la sortie avec un brouillon : « Ne pas
 *    enregistrer » quitte, fermer la boîte reste sur l'écran ;
 *  - `live` — « Créneau réservé à un live » (`showEngagedSlotWarning`), avec ou sans « Je ne peux plus
 *    assurer ce créneau » ;
 *  - `releaseLoss` — « Tu risques de perdre 2 élèves » (`showReleaseLossWarning`), avant « Tes élèves » ;
 *  - `monthlyReminder` — le rappel du mois (`MaDispoMonthlyReminder`), sur l'accueil.
 *
 * Les trois premiers étaient des feuilles (`MaDispoInfoSheet`) : ils restent une feuille au
 * téléphone, une carte centrée au large (`<Modal sizeToContent>`). Les trois autres étaient des
 * boîtes (`DialogM`) : une boîte centrée (`<Modal fitContent>`).
 *
 * Contrôlé : quelle boîte est ouverte, et l'envoi en cours, arrivent en props.
 */
import React from "react"

import { Button } from "../../atoms/Buttons/Button"
import { Stack } from "../../atoms/containers/Stack"
import { Text } from "../../atoms/typography/Text"
import { Modal } from "../../molecules/Modal"

export type TutorAvailabilityDialogProps = {
    visible: boolean
    /** La croix, le fond, le retour : on reste où l'on est. */
    onClose: () => void
    testID?: string
} & (
    | {
          kind: "startingDate"
          /** « lundi 3 novembre 2026 ». */
          dateLabel: string
          onConfirm: () => void
          saving?: boolean
      }
    | {
          kind: "continuity"
          /** « pendant 3 mois », ou « jusqu'au 30 juin, fin de l'année scolaire » au printemps. */
          duration: string
          /** `acknowledge` après la première heure posée ; `confirm` avant l'enregistrement ; `continue` à l'étape du profil. */
          mode: "acknowledge" | "confirm" | "continue"
          onConfirm?: () => void
          saving?: boolean
      }
    | { kind: "leave"; onSave: () => void; onDiscard: () => void; saving?: boolean }
    | {
          kind: "live"
          /** « à 18h30 », le début du live posé dans la case. */
          liveTimesLabel?: string
          /** Absent : l'étape du profil, où le seul chemin est la demande d'absence. */
          onRelease?: () => void
      }
    | { kind: "releaseLoss"; count: number; onContinue: () => void }
    | { kind: "monthlyReminder"; onConfirm: () => void }
)

type Content = {
    title: string
    body: React.ReactNode
    primary: { label: string; onPress: () => void; loading?: boolean }
    secondary?: { label: string; onPress: () => void }
    /** Une boîte (le `DialogM` du legacy) plutôt qu'une feuille. */
    dialog: boolean
}

const Body = ({ before, bold, after }: { before: string; bold?: string; after?: string }) => (
    <Text size="text-lg" color="text-muted">
        {before}
        {bold != null && (
            <Text size="text-lg" weight="bold">
                {bold}
            </Text>
        )}
        {after}
    </Text>
)

function contentOf(props: TutorAvailabilityDialogProps): Content {
    const { onClose } = props
    switch (props.kind) {
        case "startingDate":
            return {
                title: "Changer ta date de démarrage ?",
                body: (
                    <Body
                        before="Tes créneaux ne seront proposés aux familles qu'à partir du "
                        bold={props.dateLabel}
                        after=". Tes lives en cours ne sont pas concernés."
                    />
                ),
                primary: { label: "Confirmer", onPress: props.onConfirm, loading: props.saving },
                secondary: { label: "Annuler", onPress: onClose },
                dialog: false,
            }
        case "continuity": {
            const acknowledge = props.mode === "acknowledge"
            return {
                title: "Un créneau régulier, c'est un engagement",
                body: (
                    <Body
                        before="Un créneau que tu poses, tu t'engages à le tenir "
                        bold={props.duration}
                        after=". Les familles s'inscrivent sur la durée."
                    />
                ),
                primary: {
                    label: acknowledge ? "J'ai compris" : props.mode === "confirm" ? "Confirmer ma dispo" : "Continuer",
                    onPress: acknowledge ? onClose : (props.onConfirm ?? onClose),
                    loading: props.saving,
                },
                secondary: acknowledge ? undefined : { label: "Annuler", onPress: onClose },
                dialog: false,
            }
        }
        case "leave":
            return {
                title: "Enregistrer tes modifications ?",
                body: (
                    <Body before="Tes créneaux ont changé. Sans confirmation, les familles continueront de voir ta semaine précédente." />
                ),
                primary: { label: "Confirmer ma dispo", onPress: props.onSave, loading: props.saving },
                secondary: { label: "Ne pas enregistrer", onPress: props.onDiscard },
                dialog: false,
            }
        case "live": {
            const live = `Ce créneau accueille un live${props.liveTimesLabel ?? ""}.`
            return props.onRelease != null
                ? {
                      title: "Créneau réservé à un live",
                      body: (
                          <Body
                              before={`${live} Pour une absence ponctuelle, fais une demande d'absence depuis ce live.`}
                          />
                      ),
                      primary: { label: "Je ne peux plus assurer ce créneau", onPress: props.onRelease },
                      secondary: { label: "J'ai compris", onPress: onClose },
                      dialog: true,
                  }
                : {
                      title: "Créneau réservé à un live",
                      body: <Body before={`${live} Pour le libérer, fais une demande d'absence.`} />,
                      primary: { label: "J'ai compris", onPress: onClose },
                      dialog: true,
                  }
        }
        case "releaseLoss": {
            const several = props.count > 1
            return {
                title: several ? `Tu risques de perdre ${props.count} élèves` : "Tu risques de perdre un élève",
                body: (
                    <Body
                        before={`${several ? "Ces élèves peuvent" : "Cet élève peut"} passer à un autre tuteur. Regarde ce qui change pour chacun avant de confirmer.`}
                    />
                ),
                primary: { label: "Voir l'impact", onPress: props.onContinue },
                secondary: { label: "Revenir", onPress: onClose },
                dialog: true,
            }
        }
        case "monthlyReminder":
            return {
                title: "Confirme tes créneaux",
                body: <Body before="Une fois par semaine, pour continuer à recevoir des propositions de groupes." />,
                primary: { label: "Confirmer", onPress: props.onConfirm },
                secondary: { label: "Plus tard", onPress: onClose },
                dialog: true,
            }
    }
}

export function TutorAvailabilityDialog(props: TutorAvailabilityDialogProps) {
    const { visible, onClose, testID } = props
    const { title, body, primary, secondary, dialog } = contentOf(props)

    const footer = (
        <Stack gap="2">
            <Button label={primary.label} onPress={primary.onPress} loading={primary.loading} fullWidth />
            {secondary != null && (
                <Button label={secondary.label} variant="outline" onPress={secondary.onPress} fullWidth />
            )}
        </Stack>
    )

    return (
        <Modal
            visible={visible}
            onClose={onClose}
            title={title}
            // Une boîte pose une question : son titre passe à la ligne, jamais coupé (§ « Une étape de geste n'a qu'un en-tête »).
            steps={{ step: 1, total: 1 }}
            fitContent={dialog}
            sizeToContent={!dialog}
            footer={footer}
            testID={testID}
        >
            {body}
        </Modal>
    )
}
